import { useState, useCallback, useEffect, useRef } from 'react';
import {
  TimelinePostDetail,
  CreateTimelinePostRequest,
  UpdateTimelinePostRequest,
  GetTimelinePostsQuery,
  PostCategory,
  PostStatus,
} from '../types/timeline';
import { timelineService } from '../services/timelineService';
import { useToast } from './useToast';

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

interface CompanyStats {
  total_posts: number;
  published_posts: number;
  draft_posts: number;
  total_views: number;
  total_likes: number;
  avg_engagement_rate: number;
}

export const useTimeline = () => {
  const [posts, setPosts] = useState<TimelinePostDetail[]>([]);
  const [currentPost, setCurrentPost] = useState<TimelinePostDetail | null>(
    null
  );
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
    has_next: false,
    has_prev: false,
  });
  // lastQuery を ref として管理（依存配列の問題を回避）
  const lastQueryRef = useRef<GetTimelinePostsQuery>({});
  const { showToast } = useToast();

  // 投稿一覧取得
  const loadPosts = useCallback(
    async (query: GetTimelinePostsQuery = {}) => {
      try {
        setPostsLoading(true);
        lastQueryRef.current = query; // 現在のクエリを保存（refを使用）
        const response = await timelineService.getPosts(query);

        setPosts(response.data.posts);
        setPagination({
          current_page: response.data.pagination.current_page,
          total_pages: response.data.pagination.total_pages,
          total_count: response.data.pagination.total_count,
          per_page: response.data.pagination.per_page,
          has_next:
            response.data.pagination.current_page <
            response.data.pagination.total_pages,
          has_prev: response.data.pagination.current_page > 1,
        });
      } catch (error: any) {
        console.error('Failed to load timeline posts:', error);
        showToast('投稿一覧の取得に失敗しました', 'error');
      } finally {
        setPostsLoading(false);
      }
    },
    [showToast]
  );

  // 投稿詳細取得
  const loadPostDetail = useCallback(
    async (postId: number) => {
      try {
        setLoading(true);
        const response = await timelineService.getPostById(postId);
        setCurrentPost(response.data);
      } catch (error: any) {
        console.error('Failed to load post detail:', error);
        showToast('投稿詳細の取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  // 投稿作成
  const createPost = useCallback(
    async (postData: CreateTimelinePostRequest) => {
      try {
        setCreateLoading(true);
        const response = await timelineService.createPost(postData);

        showToast('投稿を公開しました', 'success');

        // 投稿一覧を再読み込み（最後のクエリを使用）
        await loadPosts(lastQueryRef.current);

        return response.data;
      } catch (error: any) {
        console.error('Failed to create post:', error);

        // バックエンドからの詳細なエラーメッセージを取得
        let errorMessage = '投稿の作成に失敗しました';
        if (error.response?.data?.details) {
          errorMessage = error.response.data.details;
        } else if (
          error.response?.data?.message &&
          error.response.data.message !== 'VALIDATION_ERROR'
        ) {
          errorMessage = error.response.data.message;
        }

        showToast(errorMessage, 'error');
        throw error;
      } finally {
        setCreateLoading(false);
      }
    },
    [showToast, loadPosts]
  );

  // 投稿更新
  const updatePost = useCallback(
    async (postId: number, updateData: UpdateTimelinePostRequest) => {
      try {
        setLoading(true);
        const response = await timelineService.updatePost(postId, updateData);

        showToast('投稿を更新しました', 'success');

        // 投稿一覧を再読み込み（最後のクエリを使用）
        await loadPosts(lastQueryRef.current);

        // 現在の投稿が更新対象の場合は詳細も再読み込み
        if (currentPost?.id === postId) {
          await loadPostDetail(postId);
        }

        return response.data;
      } catch (error: any) {
        console.error('Failed to update post:', error);

        // バックエンドからの詳細なエラーメッセージを取得
        let errorMessage = '投稿の更新に失敗しました';
        if (error.response?.data?.details) {
          errorMessage = error.response.data.details;
        } else if (
          error.response?.data?.message &&
          error.response.data.message !== 'VALIDATION_ERROR'
        ) {
          errorMessage = error.response.data.message;
        }

        showToast(errorMessage, 'error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [showToast, loadPosts, loadPostDetail, currentPost]
  );

  // いいね切り替え（学生のみ）
  const toggleLike = useCallback(
    async (postId: number) => {
      try {
        const response = await timelineService.toggleLike(postId);

        // 投稿一覧のいいね状態を更新
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  is_liked_by_user: response.data.is_liked,
                  like_count: response.data.like_count,
                }
              : post
          )
        );

        // 現在の投稿詳細のいいね状態を更新
        if (currentPost?.id === postId) {
          setCurrentPost((prev) =>
            prev
              ? {
                  ...prev,
                  is_liked_by_user: response.data.is_liked,
                  like_count: response.data.like_count,
                }
              : null
          );
        }

        showToast(
          response.data.is_liked
            ? 'いいねを追加しました'
            : 'いいねを削除しました',
          'success'
        );
      } catch (error: any) {
        console.error('Failed to toggle like:', error);
        showToast('いいね操作に失敗しました', 'error');
      }
    },
    [showToast, currentPost?.id]
  );

  // 投稿削除
  const deletePost = useCallback(
    async (postId: number) => {
      try {
        setLoading(true);
        await timelineService.deletePost(postId);

        showToast('投稿を削除しました', 'success');

        // 投稿一覧から削除
        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));

        // 現在の投稿が削除対象の場合はクリア
        if (currentPost?.id === postId) {
          setCurrentPost(null);
        }
      } catch (error: any) {
        console.error('Failed to delete post:', error);
        showToast('投稿の削除に失敗しました', 'error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [showToast, currentPost?.id]
  );

  // 企業統計取得
  const loadCompanyStats = useCallback(
    async (companyId: number) => {
      try {
        setLoading(true);
        const response = await timelineService.getCompanyStats(companyId);
        setCompanyStats(response.data);
      } catch (error: any) {
        console.error('Failed to load company stats:', error);
        showToast('統計データの取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  // フィルター用のヘルパー関数
  const filterPosts = useCallback(
    (category?: PostCategory, status?: PostStatus, search?: string) => {
      const query: GetTimelinePostsQuery = {};

      if (category) query.category = category;
      if (status) query.status = status;
      if (search) query.search = search;

      return loadPosts(query);
    },
    [loadPosts]
  );

  // ページネーション
  const loadPage = useCallback(
    (page: number, query: GetTimelinePostsQuery = {}) => {
      return loadPosts({ ...query, page });
    },
    [loadPosts]
  );

  // 初期データ読み込みは各コンポーネントで明示的に実行
  // （企業管理ページで他の企業の投稿が一瞬表示される問題を防ぐため）

  return {
    // State
    posts,
    currentPost,
    companyStats,
    loading,
    postsLoading,
    createLoading,
    pagination,

    // Actions
    loadPosts,
    loadPostDetail,
    createPost,
    updatePost,
    toggleLike,
    deletePost,
    loadCompanyStats,
    filterPosts,
    loadPage,

    // Utilities
    setCurrentPost,
    setPosts,
  };
};
